import { Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LlmProviderService } from '../../Services/llmProvider.service';
import { LoginService } from '../../Services/login.service';
import { SystemService } from '../../Services/system.service';
import { Message } from '../../Model/System/Message';
import {
  LLMConnectionTestResult,
  LLMMyAccess,
  LLMProvider,
  LLMProviderNames,
  LLMSelectableProvider
} from '../../Model/AI/LLMProvider';

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

@Component({
    selector: 'llm-customer-keys',
    templateUrl: 'llmCustomerKeys.component.html',
    standalone: false
})
export class LlmCustomerKeysComponent implements OnInit {
  public access: LLMMyAccess[] = [];
  public selectableProviders: { id: string; name: string }[] = [];
  public editing: LLMMyAccess = null;
  public editorVisible = false;
  public saving = false;
  public saveError: string = null;
  public testResult: LLMConnectionTestResult = null;
  private editingHasStoredToken = false;

  constructor(
    private llmProviderService: LlmProviderService,
    private loginService: LoginService,
    private systemService: SystemService,
    private translateService: TranslateService) {
    this.onEdit = this.onEdit.bind(this);
    this.onTest = this.onTest.bind(this);
    this.onDelete = this.onDelete.bind(this);
  }

  ngOnInit(): void {
    this.loadProviders();
    this.reload();
  }

  public get isManager(): boolean {
    return this.loginService.isManager;
  }

  public get tokenHelpText(): string {
    return this.editingHasStoredToken
      ? this.translateService.instant('Leer lassen, um das gespeicherte Token beizubehalten')
      : this.translateService.instant('Ihr eigenes Token beim Anbieter');
  }

  public mask(token: string): string {
    if (!token) {
      return '';
    }

    return token.startsWith('••••') ? token : `••••${token.slice(-4)}`;
  }

  public onAdd(): void {
    this.editing = {
      id: EMPTY_GUID,
      providerId: this.selectableProviders.length ? this.selectableProviders[0].id : null,
      provider: null,
      token: '',
      baseUrl: '',
      isDefault: false
    };
    this.editingHasStoredToken = false;
    this.saveError = null;
    this.editorVisible = true;
  }

  public onEdit(event: any): void {
    this.editing = { ...event.row.data, token: '' };
    this.editingHasStoredToken = !!event.row.data.token;
    this.saveError = null;
    this.editorVisible = true;
  }

  public onSave(): void {
    this.saving = true;
    this.saveError = null;

    this.llmProviderService.saveMyAccess(this.customerId, this.editing).subscribe({
      next: () => {
        this.saving = false;
        this.editorVisible = false;
        this.reload();
      },
      error: response => {
        this.saving = false;
        this.saveError = this.describe(response);
      }
    });
  }

  public onDelete(event: any): void {
    this.llmProviderService.deleteMyAccess(this.customerId, event.row.data.id).subscribe({
      next: () => this.reload(),
      error: response => this.notifyError('Zugang konnte nicht gelöscht werden: ', response)
    });
  }

  public onTest(event: any): void {
    this.testResult = null;
    this.llmProviderService.testMyAccess(this.customerId, event.row.data).subscribe({
      next: result => this.testResult = result,
      error: response => this.notifyError('Verbindungstest fehlgeschlagen: ', response)
    });
  }

  private get customerId(): string {
    return this.loginService.currentCustomer.id;
  }

  private loadProviders(): void {
    this.llmProviderService.getSelectableProviders(this.customerId).subscribe({
      next: providers => this.selectableProviders = providers.map(p => ({ id: p.id, name: this.displayName(p) })),
      error: response => this.notifyError('Anbieter konnten nicht geladen werden: ', response)
    });
  }

  private displayName(provider: LLMSelectableProvider): string {
    const known = LLMProviderNames.find(x => x.id === provider.provider);
    return known ? known.name : provider.provider;
  }

  private reload(): void {
    this.llmProviderService.getMyAccess(this.customerId).subscribe({
      next: access => this.access = access,
      error: response => this.notifyError('Zugänge konnten nicht geladen werden: ', response)
    });
  }

  private notifyError(prefix: string, response: any): void {
    this.systemService.notify(new Message(this.translateService.instant(prefix) + this.describe(response), 'error'));
  }

  private describe(response: any): string {
    return response?.error?.detail ?? response?.error?.title ?? response?.message ?? this.translateService.instant('Unbekannter Fehler');
  }
}
